import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Table,
  Button,
  Tag,
  Pagination,
  TableProps,
  Tooltip,
  Space,
  Select,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  SearchOutlined,
  DownloadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./customers.css";
import { useCallback, useEffect, useState } from "react";
import EduLearnerService from "../services/EduLearnerService";
import { IEduLearnerResponseData } from "../types/EduLearner";
import type { PaginationProps } from "antd";
import { CopyClipboard } from "../components/General/CopyClipboard";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { convertDate } from "../utils";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import _ from "lodash";
import { StartCall } from "../components/General/StartCall";
import Filter from "../components/EduLearner/Filter";
import { openNotification } from "../utils";

const { Title } = Typography;

export const EduLearnerList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastCurrentPage = location.state?.currentPage ?? 0;
  const lastPageSize = location.state?.pageSize ?? 20;
  const lastFilterText = location.state?.filterText ?? "";
  const lastSortByField = location.state?.sortByField ?? "eduCreatedAt";
  const lastSortByOrder = location.state?.sortByOrder ?? "desc";
  const lastSearchText = location.state?.searchText ?? "";

  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(lastSearchText ?? "");
  const [param, setParam] = useState(
    localStorage.getItem("eduLearnerFilterText") ||
      `?page=${lastCurrentPage}&size=${lastPageSize}&sort=${lastSortByField}&direction=${lastSortByOrder}${lastFilterText}${lastSearchText}`
  );
  const [currentPage, setCurrentPage] = useState<number>(lastCurrentPage);
  const [pageSize, setPageSize] = useState<number>(lastPageSize);
  const [sortByField, setSortByField] = useState<string>(
    lastSortByField ?? "eduCreatedAt"
  );
  const [sortByOrder, setSortByOrder] = useState<string>(
    lastSortByOrder ?? "desc"
  );
  const [eduLearners, setEduLearners] = useState<any[]>([]);
  const [filterText, setFilterText] = useState<string>(lastSearchText ?? "");
  const [value, setValue] = useState("");
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [crmStatusOptions, setCrmStatusOptions] = useState<any[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const loadFilterTextFromLocalStorage = () => {
      const storedFilterText = localStorage.getItem("eduLearnerFilterText");
      if (storedFilterText) {
        setParam(storedFilterText);
      }
    };
    loadFilterTextFromLocalStorage();
  }, []);

  useEffect(() => {
    // Load CRM Statuses
    EduLearnerService.getCrmStatuses()
      .then((response: any) => {
        const options = response.data.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setCrmStatusOptions(options);
      })
      .catch((e: Error) => {
        console.log("Error loading CRM statuses:", e);
      });
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: "Learner",
      dataIndex: "fullname",
      key: "fullname",
      fixed: "left",
      width: 240,
      sorter: true,
      render: (_, row) => {
        const statusColor = row.eduStatus === "active" ? "green" : "red";
        return (
          <div style={{ padding: '8px 6px', minHeight: '110px' }}>
            <div style={{ 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '1.4',
              color: '#262626'
            }}>
              {row.fullname}
            </div>
            <div style={{ 
              marginBottom: '6px',
              fontSize: '12px',
              wordBreak: 'break-all',
              lineHeight: '1.4'
            }}>
              <CopyClipboard text={row.email}></CopyClipboard>
            </div>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              marginBottom: '6px'
            }}>
              <span>{row.phone}</span>
              <StartCall text={row?.phone} />
            </div>
            <div style={{ marginBottom: '4px' }}>
              <Tag color={statusColor} style={{ fontSize: 10, padding: '0 4px' }}>
                {row.eduStatus}
              </Tag>
            </div>
            <div style={{ fontSize: '10px', color: '#666', lineHeight: '1.4' }}>
              <span>Created: {convertDate(row.eduCreatedAt)}</span>
              {row.eduLastLogin && (
                <>
                  <span style={{ margin: '0 4px' }}>-</span>
                  <span>Last Login: {convertDate(row.eduLastLogin)}</span>
                </>
              )}
              {!row.eduLastLogin && (
                <>
                  <span style={{ margin: '0 4px' }}>-</span>
                  <span style={{ color: '#ff4d4f' }}>No Login</span>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "CRM Status",
      dataIndex: "crmStatusName",
      key: "crmStatusName",
      width: 160,
      render: (_, row) => {
        const isUpdating = updatingStatus[row.eduLearnerId] || false;
        return (
          <Select
            value={row.crmStatusId}
            onChange={(value) => handleChangeCrmStatus(row.eduLearnerId, value)}
            options={crmStatusOptions}
            style={{ width: '100%' }}
            size="small"
            placeholder="Select Status"
            loading={isUpdating}
            disabled={isUpdating}
            showSearch
            filterOption={(input, option: any) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: "App Packages",
      key: "appPackages",
      width: 200,
      render: (_, row) => {
        const hasPackages = row.latestOneamzPackageName || row.latestMongerPackageName;
        
        if (!hasPackages) {
          return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
        }
        
        return (
          <div style={{ fontSize: 11, lineHeight: '1.6' }}>
            {row.latestOneamzPackageName && (
              <div style={{ marginBottom: 4 }}>
                <strong style={{ color: '#1890ff' }}>ONEAMZ:</strong>{' '}
                <span>{row.latestOneamzPackageName}</span>
              </div>
            )}
            {row.latestMongerPackageName && (
              <div>
                <strong style={{ color: '#52c41a' }}>MONGERLAB:</strong>{' '}
                <span>{row.latestMongerPackageName}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Groups",
      dataIndex: "groups",
      key: "groups",
      width: 220,
      render: (_, row) => {
        if (!row.groups || row.groups.length === 0) return "-";
        return (
          <div>
            {row.groups.map((group: any, index: number) => {
              const endDate = dayjs(group.groupEndDate);
              const today = dayjs();
              const daysRemaining = endDate.diff(today, "day");
              const color = daysRemaining < 0 ? "red" : daysRemaining < 30 ? "orange" : "green";
              
              return (
                <div key={index} style={{ marginBottom: 4 }}>
                  <Tooltip title={`End: ${convertDate(group.groupEndDate)} (${daysRemaining} days ${daysRemaining < 0 ? 'expired' : 'remaining'})`}>
                    <Tag color={color} style={{ fontSize: 10 }}>
                      {group.groupName} ({daysRemaining}d)
                    </Tag>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Courses",
      dataIndex: "courses",
      key: "courses",
      width: 220,
      render: (_, row) => {
        if (!row.courses || row.courses.length === 0) return "-";
        return (
          <div>
            {row.courses.map((course: any, index: number) => (
              <div key={index} style={{ marginBottom: 4 }}>
                <Tooltip
                  title={`${course.lessonCompletedCount}/${course.lessonTotalCount} lessons`}
                >
                  <Tag
                    color={
                      course.completionPercentage >= 80
                        ? "green"
                        : course.completionPercentage >= 50
                        ? "orange"
                        : "red"
                    }
                    style={{ fontSize: 10 }}
                  >
                    {course.courseName} ({course.completionPercentage}%)
                  </Tag>
                </Tooltip>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "eduLearnerId",
      width: 120,
      align: "center",
      fixed: "right",
      title: "Actions",
      dataIndex: "eduLearnerId",
      render: (_: any, record: any) => {
        return (
          <div className="action-buttons">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                navigate(`/edu-learners/detail/${record.eduLearnerId}`, {
                  state: {
                    currentPage: currentPage,
                    pageSize: pageSize,
                    filterText: filterText,
                    sortByField: sortByField,
                    sortByOrder: sortByOrder,
                    searchText: searchText,
                  },
                });
              }}
            >
              Detail
            </Button>
          </div>
        );
      },
    },
  ];

  const handleSearch = useCallback((searchValue: string) => {
    if (searchValue === "") {
      setSearchText("");
    } else {
      let searchQuery = `&search=${searchValue}`;
      setSearchText(searchQuery);
    }
  }, []);

  const debouncedSearch = useCallback(_.debounce(handleSearch, 500), [
    handleSearch,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    debouncedSearch(newValue);
  };

  const handleOnChangeFilter = (filterForm: any) => {
    let filterQuery = "";

    if (filterForm.eduStatus && filterForm.eduStatus !== null) {
      filterQuery += `&eduStatus=${filterForm.eduStatus}`;
    }

    if (filterForm.crmStatusId && filterForm.crmStatusId !== null) {
      filterQuery += `&crmStatusId=${filterForm.crmStatusId}`;
    }

    if (filterForm.groupName && filterForm.groupName !== null) {
      filterQuery += `&groupName=${encodeURIComponent(filterForm.groupName)}`;
    }

    if (filterForm.courseName && filterForm.courseName !== null) {
      filterQuery += `&courseName=${encodeURIComponent(filterForm.courseName)}`;
    }

    if (filterForm.hasLastLogin && filterForm.hasLastLogin !== null) {
      if (filterForm.hasLastLogin === "true") {
        filterQuery += `&hasLastLogin=true`;
      } else {
        filterQuery += `&hasLastLogin=false`;
      }
    }

    if (filterForm.packageFilters && filterForm.packageFilters.length > 0) {
      filterForm.packageFilters.forEach((filter: string) => {
        if (filter === "hasOneamz") {
          filterQuery += `&hasOneamzPackage=true`;
        } else if (filter === "noOneamz") {
          filterQuery += `&hasOneamzPackage=false`;
        } else if (filter === "hasMonger") {
          filterQuery += `&hasMongerPackage=true`;
        } else if (filter === "noMonger") {
          filterQuery += `&hasMongerPackage=false`;
        }
      });
    }

    setFilterText(filterQuery);
  };

  const handleChangeCrmStatus = (eduLearnerId: string, statusId: number) => {
    setUpdatingStatus((prev) => ({ ...prev, [eduLearnerId]: true }));
    
    EduLearnerService.updateCrmStatus(eduLearnerId, { crmStatusId: statusId })
      .then(() => {
        openNotification("success", "Success", "CRM Status updated");
        
        // Update local state
        setEduLearners((prevLearners) =>
          prevLearners.map((learner) =>
            learner.eduLearnerId === eduLearnerId
              ? {
                  ...learner,
                  crmStatusId: statusId,
                  crmStatusName: crmStatusOptions.find((opt) => opt.value === statusId)?.label || "",
                }
              : learner
          )
        );
        
        setUpdatingStatus((prev) => ({ ...prev, [eduLearnerId]: false }));
      })
      .catch(() => {
        openNotification("error", "Error", "Failed to update CRM Status");
        setUpdatingStatus((prev) => ({ ...prev, [eduLearnerId]: false }));
      });
  };

  const removeFilter = (filterName: string) => {
    const updatedFilters = filterOptions.filter(
      (filter) => filter.name !== filterName
    );
    setFilterOptions(updatedFilters);

    const filterMap: any = {};
    const packageFilters: string[] = [];
    
    updatedFilters.forEach((filter) => {
      if (filter.name === "Status") filterMap.eduStatus = filter.value;
      if (filter.name === "CRM Status") {
        const statusOption = filter.value;
        filterMap.crmStatusId = statusOption;
      }
      if (filter.name === "Group") filterMap.groupName = filter.value;
      if (filter.name === "Course") filterMap.courseName = filter.value;
      if (filter.name === "Last Login") {
        filterMap.hasLastLogin = filter.value === "Has Login" ? "true" : "false";
      }
      if (filter.name === "App Packages") {
        if (filter.value === "Has Oneamz") packageFilters.push("hasOneamz");
        if (filter.value === "No Oneamz") packageFilters.push("noOneamz");
        if (filter.value === "Has MongerLab") packageFilters.push("hasMonger");
        if (filter.value === "No MongerLab") packageFilters.push("noMonger");
      }
    });

    if (packageFilters.length > 0) {
      filterMap.packageFilters = packageFilters;
    }

    handleOnChangeFilter(filterMap);
  };

  function downloadExcel(jsonData: any[]) {
    const data = jsonData.map((learner: any) => {
      return {
        fullname: learner.fullname,
        email: learner.email,
        phone: learner.phone,
        eduStatus: learner.eduStatus,
        crmStatusName: learner.crmStatusName || "",
        latestOneamzPackageName: learner.latestOneamzPackageName || "",
        latestMongerPackageName: learner.latestMongerPackageName || "",
        groups: learner.groups?.map((g: any) => g.groupName).join(", ") || "",
        courses: learner.courses?.map((c: any) => c.courseName).join(", ") || "",
        eduLastLogin: convertDate(learner.eduLastLogin),
        eduCreatedAt: convertDate(learner.eduCreatedAt),
      };
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "binary" });

    function s2ab(s: string) {
      const buffer = new ArrayBuffer(s.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
      }
      return buffer;
    }

    const blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" });
    saveAs(blob, "edu-learner-list.xlsx");
  }

  const paginationOnChange: PaginationProps["onChange"] = (page, pageSize) => {
    setCurrentPage(page - 1);
    setPageSize(pageSize);
  };

  const handleTableChange: TableProps<IEduLearnerResponseData>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const sortObj: any = sorter;

    if (sortObj.order === undefined) {
      setSortByField("eduCreatedAt");
      setSortByOrder("desc");
    } else {
      let sortType = "asc";
      if (sortObj.order === "descend") {
        sortType = "desc";
      }
      setSortByField(sortObj.field);
      setSortByOrder(sortType);
    }
  };

  useEffect(() => {
    const newParam = `?page=${currentPage}&size=${pageSize}&sort=${sortByField}&direction=${sortByOrder}${filterText}${searchText}`;
    setParam(newParam);
    localStorage.setItem("eduLearnerFilterText", newParam);
  }, [currentPage, pageSize, sortByField, sortByOrder, filterText, searchText]);

  useEffect(() => {
    setLoading(true);
    EduLearnerService.getAll(param)
      .then((response: any) => {
        setEduLearners(response.data.data);
        setTotalItems(response.data.totalElements);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  }, [param]);

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4}>EDU Learners</Title>
              </Col>
              <Col>
                <Space>
                  <Input
                    placeholder="Search by name, email, phone"
                    prefix={<SearchOutlined />}
                    onChange={handleChange}
                    style={{ width: 300 }}
                    allowClear
                  />
                  <Filter
                    onChangeFilter={handleOnChangeFilter}
                    setFilterOptions={setFilterOptions}
                    param={param}
                  />
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {filterOptions.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card>
              <Space wrap>
                {filterOptions.map((filter, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => removeFilter(filter.name)}
                    closeIcon={<CloseOutlined />}
                  >
                    {filter.name}: {filter.value}
                  </Tag>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" style={{ marginBottom: 16 }}>
              <Col>
                <span>Total: {totalItems}</span>
              </Col>
              <Col>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => downloadExcel(eduLearners)}
                  disabled={eduLearners.length === 0}
                >
                  Export Excel
                </Button>
              </Col>
            </Row>
            <Table
              columns={columns}
              dataSource={eduLearners}
              rowKey="eduLearnerId"
              pagination={false}
              loading={loading}
              onChange={handleTableChange}
              scroll={{ x: 1400 }}
            />
            <Row justify="end" style={{ marginTop: 16 }}>
              <Pagination
                current={currentPage + 1}
                pageSize={pageSize}
                total={totalItems}
                onChange={paginationOnChange}
                showSizeChanger
                showTotal={(total) => `Total ${total} items`}
              />
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};
